/**
 * LeadFormDialog
 *
 * Quick "log a lead" form launched from the homepage. Lead = inquiry
 * from outside the shop (phone, referral, exec visit, site visit).
 * Open to every logged-in user. Admin/Manager manage them in the
 * admin panel. WhatsApp reminders fire on the follow-up date.
 *
 * Lead Owner: only an active salesperson (staff, creator, manager) owns a
 * lead. A salesperson logs leads as themselves (field locked) unless they
 * have `leads.reassign` (admin / manager by default). Anyone who is not a
 * salesperson (admin, dispatcher, delivery, stock manager) must pick an
 * owner. The server enforces the same rule.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { api, API, addDaysIst, useAuth } from '../../lib/store';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Phone, Camera, X } from 'lucide-react';

const SOURCES = ['Company Exec', 'Carpenter', 'Architect', 'Site Visit', 'Referral', 'Social Media'];
const MAX_SITE_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// NEW leads get a follow-up date pre-set to today+3 (IST) so
// staff always have a reasonable reminder. Field is editable and can
// be cleared to opt out of a follow-up.
const followUpDefaultIst = () => addDaysIst(3);

const initial = {
  customer_name: '',
  customer_phone: '',
  location: '',
  source: '',
  architect_name: '',
  carpenter_name: '',
  follow_up_date: '',
  notes: '',
  // Lead Owner (active staff / creator / manager). Set to the signed-in
  // user when the dialog opens.
  owner_id: '',
};

// Fresh-state builder used by initial render AND every re-open so the
// dialog opens clean each time.
const buildInitial = (ownerId = '') => ({ ...initial, follow_up_date: followUpDefaultIst(), owner_id: ownerId });

export default function LeadFormDialog({ open, onOpenChange, onCreated }) {
  const { user, can } = useAuth();
  const isSalesperson = ['staff', 'creator', 'manager'].includes(user?.role);
  const canReassign = can('leads.reassign') || !isSalesperson;
  const myId = user?.id || '';
  const [form, setForm] = useState(() => buildInitial(myId));
  const [submitting, setSubmitting] = useState(false);
  const [owners, setOwners] = useState([]);
  // Site photos are staged client-side then uploaded AFTER the lead is
  // created (so the multipart POST has a lead_id). Failures during
  // upload toast an error but the lead is already saved.
  const [pendingPhotos, setPendingPhotos] = useState([]);        // File[]
  const [pendingPreviews, setPendingPreviews] = useState([]);    // objectURL[]

  useEffect(() => {
    if (open) {
      setForm(buildInitial(myId));
      setPendingPhotos([]);
      setPendingPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
    }
  }, [open, myId]);

  // Revoke object URLs on unmount so we don't leak preview blobs.
  useEffect(() => () => {
    pendingPreviews.forEach((u) => URL.revokeObjectURL(u));
  }, [pendingPreviews]);

  // Active salespeople for the Lead Owner picker. Cached after first open;
  // only needed when this user may choose someone else.
  useEffect(() => {
    if (!open || !canReassign || owners.length > 0) return;
    api.getLeadOwners()
      .then((res) => setOwners(res.data?.owners || []))
      .catch(() => setOwners([]));
  }, [open, canReassign, owners.length]);

  // An admin or dispatcher is not a salesperson (not in the list): no
  // default owner, they must choose one.
  const ownerValue = canReassign
    ? (owners.some((o) => o.id === form.owner_id) ? form.owner_id : undefined)
    : myId;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePickPhotos = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (chosen.length === 0) return;
    const remainingSlots = MAX_SITE_IMAGES - pendingPhotos.length;
    if (remainingSlots <= 0) {
      toast.error(`Max ${MAX_SITE_IMAGES} site photos per lead`);
      e.target.value = '';
      return;
    }
    const accepted = [];
    const previews = [];
    for (const file of chosen.slice(0, remainingSlots)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: not an image`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error(`${file.name}: exceeds 5 MB`);
        continue;
      }
      accepted.push(file);
      previews.push(URL.createObjectURL(file));
    }
    if (chosen.length > remainingSlots) {
      toast.info(`Only kept ${remainingSlots} — cap is ${MAX_SITE_IMAGES}`);
    }
    setPendingPhotos((prev) => [...prev, ...accepted]);
    setPendingPreviews((prev) => [...prev, ...previews]);
    e.target.value = '';  // let user re-pick the same file after remove
  };

  const removePendingPhoto = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => {
      const removed = prev[idx];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (canReassign && !ownerValue) {
      toast.error('Choose the Lead Owner');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.createLead({ ...form, owner_id: canReassign ? ownerValue : myId });
      const createdLead = res.data;
      // Upload staged photos (best-effort — a failure here doesn't undo
      // the saved lead; user can retry from the edit dialog).
      if (pendingPhotos.length > 0) {
        try {
          const fd = new FormData();
          pendingPhotos.forEach((file) => fd.append('files', file, file.name));
          const token = localStorage.getItem('heda_token') || '';
          await axios.post(`${API}/leads/${createdLead.id}/images`, fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
        } catch (upErr) {
          toast.error(upErr.response?.data?.detail || 'Photos upload failed — lead is saved');
        }
      }
      toast.success('Lead saved');
      setForm(buildInitial(myId));
      setPendingPhotos([]);
      setPendingPreviews((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
      onOpenChange(false);
      if (onCreated) onCreated(createdLead);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-lg max-h-[92vh] flex flex-col" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-heda-gold" /> Log a New Lead
          </DialogTitle>
          <p className="text-xs text-heda-sub mt-1">
            For inquiries that haven&apos;t visited the shop yet. Only the name is required.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-1 space-y-3 [&_input]:min-h-11 sm:[&_input]:min-h-10">
          <div>
            <Label htmlFor="lf-name" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Customer Name <span className="text-red-700">*</span></Label>
            <Input className="rounded-xl bg-heda-cream/50"
              id="lf-name"
              value={form.customer_name}
              onChange={set('customer_name')}
              placeholder=""
              autoFocus
              required
              data-testid="lead-form-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lf-phone" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Contact Number</Label>
              <Input className="rounded-xl bg-heda-cream/50"
                id="lf-phone"
                type="tel"
                value={form.customer_phone}
                onChange={set('customer_phone')}
                placeholder=""
                data-testid="lead-form-phone"
              />
            </div>
            <div>
              <Label htmlFor="lf-source" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Source</Label>
              <Select value={form.source} onValueChange={setSel('source')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" id="lf-source" data-testid="lead-form-source"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="lf-location" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Location</Label>
            <Input className="rounded-xl bg-heda-cream/50"
              id="lf-location"
              value={form.location}
              onChange={set('location')}
              placeholder=""
              data-testid="lead-form-location"
            />
          </div>

          <div>
            <Label htmlFor="lf-owner" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Lead Owner</Label>
            {canReassign ? (
              <Select value={ownerValue} onValueChange={setSel('owner_id')}>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" id="lf-owner" data-testid="lead-form-owner">
                  <SelectValue placeholder="Choose the salesperson" />
                </SelectTrigger>
                <SelectContent>
                  {owners.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`lead-form-owner-option-${o.id}`}>
                      {o.name}{o.id === myId ? ' (me)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={myId || undefined} disabled>
                <SelectTrigger className="rounded-xl bg-heda-cream/50" id="lf-owner" data-testid="lead-form-owner" title="Leads you log are yours">
                  <SelectValue placeholder={user?.name || 'Me'} />
                </SelectTrigger>
                <SelectContent>
                  {myId && <SelectItem value={myId}>{user?.name || 'Me'}</SelectItem>}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lf-architect" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Architect Name <span className="text-heda-mute">(optional)</span></Label>
              <Input className="rounded-xl bg-heda-cream/50"
                id="lf-architect"
                value={form.architect_name}
                onChange={set('architect_name')}
                placeholder=""
                data-testid="lead-form-architect"
              />
            </div>
            <div>
              <Label htmlFor="lf-carpenter" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Carpenter Name <span className="text-heda-mute">(optional)</span></Label>
              <Input className="rounded-xl bg-heda-cream/50"
                id="lf-carpenter"
                value={form.carpenter_name}
                onChange={set('carpenter_name')}
                placeholder=""
                data-testid="lead-form-carpenter"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lf-followup" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Follow-up Date</Label>
              <Input className="rounded-xl bg-heda-cream/50"
                id="lf-followup"
                type="date"
                value={form.follow_up_date}
                onChange={set('follow_up_date')}
                data-testid="lead-form-followup"
              />
            </div>
          </div>

          <div>
            <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-sky-700" />
              Site Photos <span className="text-heda-mute">(optional · up to {MAX_SITE_IMAGES})</span>
            </Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="lead-form-site-photos">
              {pendingPreviews.map((url, idx) => (
                <div
                  key={url}
                  className="relative w-16 h-16 rounded-xl overflow-hidden border border-heda-line bg-heda-cream"
                  data-testid={`lead-form-site-photo-preview-${idx}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePendingPhoto(idx)}
                    className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white rounded-full w-4 h-4 flex items-center justify-center"
                    aria-label="Remove photo"
                    data-testid={`lead-form-site-photo-remove-${idx}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              {pendingPhotos.length < MAX_SITE_IMAGES && (
                <label
                  className="w-16 h-16 rounded-xl border border-dashed border-heda-line bg-heda-cream flex flex-col items-center justify-center cursor-pointer hover:bg-heda-cream/60"
                  data-testid="lead-form-site-photo-add"
                >
                  <Camera className="w-4 h-4 text-heda-sub" />
                  <span className="text-[9px] text-heda-sub mt-0.5">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePickPhotos}
                    data-testid="lead-form-site-photo-input"
                  />
                </label>
              )}
            </div>
            <p className="text-[9px] text-heda-mute mt-1">JPG / PNG / WEBP · max 5 MB each · uploaded after the lead is saved.</p>
          </div>

          <div>
            <Label htmlFor="lf-notes" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Notes</Label>
            <Textarea className="rounded-xl bg-heda-cream/50"
              id="lf-notes"
              value={form.notes}
              onChange={set('notes')}
              placeholder=""
              rows={3}
              data-testid="lead-form-notes"
            />
          </div>
        </form>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-heda-line gap-2 sm:gap-0">
          <Button className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="lead-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="rounded-xl bg-heda-ink hover:bg-heda-ink text-white"
            disabled={submitting}
            data-testid="lead-form-submit"
          >
            {submitting ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Saving…</> : 'Save Lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
